'use client';

import { c as _c } from "react/compiler-runtime";
import type { JSX } from 'react';
import { useRef } from 'react';
import { Link } from '@/i18n/navigation';

/**
 * Real prefetching `<Link>` navigation for a cmdk `CommandItem`.
 *
 * cmdk's `CommandPrimitive.Item` is not an anchor and does not support
 * `asChild`, so a search result row can't simply BE a `<Link>`. This hook gives
 * each row a `pointer-events-none` overlay `<Link>` plus an `onSelect` handler:
 *
 *   - The overlay is a real `<a>` in the DOM, so Next.js viewport-prefetches the
 *     destination RSC (and clicking it is a soft nav) — the win we want over the
 *     old imperative router push.
 *   - `pointer-events-none` means the mouse only ever lands on the cmdk item, so
 *     cmdk owns selection exactly as before (no double-fire). Both a mouse click
 *     and a keyboard Enter route through `onSelect`, which runs `onCommit` (the
 *     search-modal teardown — recents + close) and then activates the anchor via
 *     `ref.click()` (NetworkLink forwards its ref to the underlying `<a>`).
 *
 * The host `CommandItem` must be positioned (`relative`) so the overlay's
 * `absolute inset-0` covers the row.
 */
export function useCommandLink(href, onCommit) {
  const $ = _c(7);
  const ref = useRef(null);
  let t0;
  if ($[0] !== onCommit) {
    t0 = function onSelect() {
      ref.current?.click();
      onCommit();
    };
    $[0] = onCommit;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const onSelect = t0;
  let t1;
  if ($[2] !== href) {
    t1 = <Link ref={ref} href={href} aria-hidden="true" tabIndex={-1} className="pointer-events-none absolute inset-0" />;
    $[2] = href;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const overlay = t1;
  let t2;
  if ($[4] !== onSelect || $[5] !== overlay) {
    t2 = {
      onSelect,
      overlay
    };
    $[4] = onSelect;
    $[5] = overlay;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}